import React from 'react';
import { motion } from 'framer-motion';
import { Star, Facebook, Instagram, Globe, ShieldCheck } from 'lucide-react';
import { SectionLabel } from './shared';
const reviewStats = [{
  metric: '88%',
  label: 'Recommend on Facebook',
  note: 'Customers consistently highlight responsive service and clean installs.'
}, {
  metric: '5.0',
  label: 'Average review on Angi',
  note: 'Praised for professional installation, clean wiring, and clear walkthroughs.'
}, {
  metric: '15+',
  label: 'Public reviews online',
  note: 'Verified across Facebook, Angi, Yellow Pages, and other directories.'
}, {
  metric: 'CA Lic. 7220',
  label: 'Licensed and insured',
  note: 'Operating as a fully licensed California security contractor.'
}];
const testimonials = [{
  quote: 'Watchdog walked the property, designed exactly what we needed, and explained everything before they left. The cleanest install we have had.',
  name: 'Local business owner',
  source: 'Angi'
}, {
  quote: 'Super responsive when we had questions. Cameras and access control work flawlessly across both of our locations.',
  name: 'Multi-site operator',
  source: 'Facebook'
}, {
  quote: 'Real local team. They actually pick up the phone and show up when something needs attention.',
  name: 'Homeowner',
  source: 'Google'
}];
const socialLinks = [{
  name: 'Facebook',
  handle: '@watchdogsmartalarms',
  url: 'https://www.facebook.com/watchdogsmartalarms/',
  icon: Facebook
}, {
  name: 'Instagram',
  handle: '@watchdogsecurity1',
  url: 'https://www.instagram.com/watchdogsecurity1/',
  icon: Instagram
}, {
  name: 'Customer Portal',
  handle: 'Alarm.com',
  url: 'https://www.alarm.com/US/watchdogsmarthome?home=1',
  icon: Globe
}];
export function SocialProof() {
  return <section id="reviews" className="bg-white py-24" data-id="element-150">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8" data-id="element-151">
        <div className="max-w-3xl" data-id="element-152">
          <SectionLabel data-id="element-153">Trusted locally</SectionLabel>
          <h2 className="text-4xl font-black tracking-tight sm:text-5xl" data-id="element-154">
            Real reviews from real Northern California customers.
          </h2>
          <p className="mt-6 text-base leading-7 text-slate-600 sm:text-lg sm:leading-8" data-id="element-155">
            Watchdog Security has built a strong reputation across local
            businesses and homes — verified by reviews, listings, and licensing.
          </p>
        </div>

        {/* Stat cards */}
        <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4" data-id="element-156">
          {reviewStats.map((item, index) => <motion.div key={item.label} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.06
        }} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm sm:rounded-[2rem] sm:p-7" data-id="element-157">
              <p className="text-2xl font-black text-slate-950 sm:text-4xl" data-id="element-158">
                {item.metric}
              </p>
              <h3 className="mt-3 text-sm font-black sm:mt-4 sm:text-lg" data-id="element-159">
                {item.label}
              </h3>
              <p className="mt-2 text-xs leading-5 text-slate-600 sm:mt-3 sm:text-sm sm:leading-6" data-id="element-160">
                {item.note}
              </p>
            </motion.div>)}
        </div>

        {/* Testimonials */}
        <div className="mt-12 grid gap-5 md:grid-cols-3" data-id="element-161">
          {testimonials.map((t, i) => <motion.div key={t.quote} initial={{
          opacity: 0,
          y: 18
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.06
        }} className="flex flex-col rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm" data-id="element-162">
              <div className="flex gap-1 text-sky-500" data-id="element-163">
                {Array.from({
              length: 5
            }).map((_, idx) => <Star key={idx} className="h-4 w-4 fill-sky-500 text-sky-500" data-id="element-164" />)}
              </div>
              <p className="mt-5 flex-1 text-base leading-7 text-slate-700" data-id="element-165">
                “{t.quote}”
              </p>
              <div className="mt-6 border-t border-slate-200 pt-4 text-sm" data-id="element-166">
                <p className="font-black text-slate-900" data-id="element-167">{t.name}</p>
                <p className="text-slate-500" data-id="element-168">via {t.source}</p>
              </div>
            </motion.div>)}
        </div>

        {/* Social row */}
        <div className="mt-12 rounded-[2rem] bg-slate-950 p-6 text-white sm:p-8" data-id="element-169">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between" data-id="element-170">
            <div data-id="element-171">
              <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-black uppercase tracking-wider text-sky-200" data-id="element-172">
                <ShieldCheck className="h-3.5 w-3.5" data-id="element-173" /> Connect with Watchdog
              </div>
              <h3 className="text-2xl font-black sm:text-3xl" data-id="element-174">
                Follow our work and see new installs.
              </h3>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-id="element-175">
              {socialLinks.map(s => {
              const Icon = s.icon;
              return <a key={s.name} href={s.url} target="_blank" rel="noreferrer" className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.06] p-4 transition hover:bg-sky-400 hover:text-slate-950" data-id="element-176">
                    <div className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-sky-400 text-slate-950 group-hover:bg-slate-950 group-hover:text-sky-300" data-id="element-177">
                      <Icon className="h-5 w-5" data-id="element-178" />
                    </div>
                    <div data-id="element-179">
                      <p className="text-sm font-black" data-id="element-180">{s.name}</p>
                      <p className="text-xs text-slate-400 group-hover:text-slate-700" data-id="element-181">
                        {s.handle}
                      </p>
                    </div>
                  </a>;
            })}
            </div>
          </div>
        </div>
      </div>
    </section>;
}